import { Logger, NOOP_PROMISE_VOID, Presenter } from 'wdc-cube'

import { WaitingScope } from '../../the-connection/tc_scopes'
import ConfigurationPresenter from './presenter'
import {
    EditableNotificationFields,
    NotificationStyle,
    NotificationTabScope,
    NotificationUserRow,
    UpdateNotificationRow
} from './scopes'

import { GroupManagerService } from '../group-manager/gm_service'
import { UserManagerService } from '../user-manager/um_service'
import { NotificationManagerService } from './notification-manager.service'
import { parseLocalDateNoTZ } from 'src/utils/formatter-utils'
import { NotificationTypesManagerService } from './notification-types-manager.service'

const LOG = Logger.get('NotificationTabPresenter')
const service = NotificationManagerService.singleton()
const groupManagerService = GroupManagerService.singleton()
const userManagerService = UserManagerService.singleton()
const typesService = NotificationTypesManagerService.singleton()

export class NotificationTabPresenter extends Presenter<NotificationTabScope, ConfigurationPresenter> {
    // :: Constructor

    public constructor(owner: ConfigurationPresenter) {
        super(owner, owner.scope.notificationTab)
        this.__parent = owner
    }

    // :: Instance

    private readonly __parent: ConfigurationPresenter

    private _yesAction = NOOP_PROMISE_VOID

    // :: Methods

    public override release() {
        super.release()
    }

    async initialize() {
        LOG.debug('initialize')

        this.scope.onFieldChanged = this.action(this.handleFieldChanged)
        this.scope.onGroupsChanged = this.action(this.handleGroupsChanged)
        this.scope.onUsersChanged = this.action(this.handleUsersChanged)
        this.scope.onAddSelectedUser = this.action(this.handleAddSelectedUser)

        this.scope.onFind = this.action(this.handleFind)
        this.scope.onEdit = this.action(this.handleEdit)
        this.scope.onDelete = this.action(this.handleDelete)
        this.scope.onDeleteUser = this.action(this.handleDeleteUser)
        this.scope.onNew = this.action(this.handleNew)
        this.scope.onYesNoResponse = this.action(this.handYesNoResponse)

        this.scope.onTypesChanged = this.action(this.handleTypesChanged)

        this.scope.onClose = this.action(this.handleClose)
        this.scope.onSave = this.action(this.handleSave)

        this.#loadGrupos()
        this.#loadUsers()
        this.action(this.handleTypesChanged)()
    }

    async onLoadData() {
        await this.handleFind(this.scope.filter)
    }

    async #loadGrupos() {
        LOG.debug('loadGrupos')
        const grupos = await groupManagerService.fetchGroups()
        this.scope.allGroups = grupos
    }

    async #loadUsers() {
        LOG.debug('loadUsers')
        const users = await userManagerService.fetchSimpleUsersWithGroups()
        this.scope.allUsers = users
    }

    private async handleFind(nameFilter?: string) {
        try {
            this.scope.waiting = new WaitingScope()
            this.scope.filter = nameFilter || ''
            const rows = await service.fetchAll(this.scope.filter)
            this.scope.notifications = rows
        } catch (caught) {
            LOG.error('handleFind', caught)
            throw caught
        } finally {
            this.scope.waiting = undefined
        }
    }

    private async handleEdit(notificationId: number) {
        await this.buscarSelecionado(notificationId)
        this.scope.tab = 'form'
    }

    private async handleDelete(notificationId: number) {
        this.scope.askYesOrNo = 'Deseja excluir a Notificação?'
        this._yesAction = async () => {
            try {
                await service.delete(notificationId)
                await this.handleFind(this.scope.filter)
                this._yesAction = NOOP_PROMISE_VOID
                this.__parent.app.alert('success', 'Sucesso', 'Notificação excluída com sucesso')
            } catch (error) {
                LOG.caught(error)
                this.__parent.app.alert(
                    'error',
                    'Problemas',
                    'Não foi possível excluir a notificação. Tente mais tarde'
                )
            }
        }
    }

    private async handleDeleteUser(userId: number) {
        this.scope.askYesOrNo = 'Deseja excluir o Usuário da Notificação?'
        this._yesAction = async () => {
            try {
                this.scope.users = this.scope.users.filter((u) => u.userId !== userId)
                this._yesAction = NOOP_PROMISE_VOID
                this.__parent.app.alert(
                    'success',
                    'Sucesso',
                    'Usuário removido. Para aplicar as alterações, salve a notificação!'
                )
            } catch (error) {
                LOG.caught(error)
                this.__parent.app.alert(
                    'error',
                    'Problemas',
                    'Não foi possível excluir o usuário da notificação. Tente mais tarde'
                )
            }
        }
    }

    protected async handYesNoResponse(yes: boolean) {
        LOG.debug(`handYesNoResponse: yes=${yes}`)
        this.scope.askYesOrNo = ''
        if (yes) {
            await this._yesAction()
        }
        this.scope.update()
    }

    private async handleTypesChanged() {
        this.scope.allNotificationTypes = await typesService.fetchAll(!this.scope.notificationId)
    }

    private async handleNew() {
        this.scope.notificationId = undefined
        this.scope.title = ''
        this.scope.summary = ''
        this.scope.content = ''
        this.scope.style = NotificationStyle.INFO
        this.scope.canMarkAsRead = true
        this.scope.active = true
        this.scope.alert = false
        this.scope.typeId = null
        this.scope.toAllUsers = true
        this.scope.expiresIn = undefined
        this.scope.groups = []
        this.scope.users = []

        this.scope.tab = 'form'
        await this.handleTypesChanged()
    }

    private async handleClose() {
        this.scope.tab = 'list'
    }

    private async handleSave() {
        try {
            if (!this.scope.typeId) {
                this.__parent.app.notify('warning', 'Tipo da notificação deve ser informado')
                return
            }
            if (!this.scope.title) {
                this.__parent.app.notify('warning', 'Título da notificação deve ser informado')
                return
            }
            if (!this.scope.summary) {
                this.__parent.app.notify('warning', 'Resumo da notificação deve ser informado')
                return
            }
            if (!this.scope.content) {
                this.__parent.app.notify('warning', 'Conteúdo da notificação deve ser informado')
                return
            }

            const formData = {
                title: this.scope.title,
                summary: this.scope.summary,
                content: this.scope.content,
                notificationStyle: this.scope.style,
                canMarkAsRead: this.scope.canMarkAsRead,
                active: this.scope.active,
                alert: this.scope.alert,
                toAllUsers: this.scope.toAllUsers,
                expiresIn: this.scope.expiresIn,
                notificationDate: this.scope.notificationDate,
                groups: this.scope.groups,
                users: this.scope.users,
                typeId: this.scope.typeId
            } as UpdateNotificationRow

            const retSalvar = this.scope.notificationId
                ? await service.update(this.scope.notificationId, formData)
                : await service.create(formData)

            if (retSalvar) {
                this.action(this.handleClose)()
                await this.handleFind()
                this.__parent.app.notify('success', 'Salvo com sucesso')
            }
        } catch (caught) {
            LOG.error('handleSave', caught)
            throw caught
        }
    }

    async handleFieldChanged<K extends keyof EditableNotificationFields>(field: K, value: NotificationTabScope[K]) {
        this.scope[field] = value
    }

    async handleGroupsChanged(idGroups: (number | undefined)[]) {
        idGroups
            .filter((id) => id && !this.scope.groups?.some((g) => g.groupId === id))
            .forEach((id) => {
                const group = this.scope.allGroups.find((g) => g.id === id)
                if (group) {
                    this.scope.groups = [
                        ...(this.scope.groups || []),
                        { groupId: group.id as number, groupName: group.name }
                    ]

                    const usersInGroup = this.scope.allUsers.filter(
                        (u) =>
                            u.groups?.some((g) => g.id === group.id) &&
                            !this.scope.users?.some((u2) => u2.userId === u.id)
                    )
                    if (usersInGroup.length > 0) {
                        this.scope.users = [
                            ...(this.scope.users || []),
                            ...usersInGroup.map(
                                (u) =>
                                    ({
                                        userId: u.id,
                                        userName: u.name,
                                        groups: u.groups?.map((g) => g.name).join(', ')
                                    }) as NotificationUserRow
                            )
                        ]
                    }
                }
            })

        if (!this.scope.groups.every((g) => idGroups.includes(g.groupId))) {
            this.scope.askYesOrNo =
                'Deseja excluir o grupo da notificação? Isso removerá a notificação dos usuários desse grupo.'
            this._yesAction = async () => {
                this.scope.groups = this.scope.groups.filter((g) => idGroups.includes(g.groupId))
                const removedGroupIds = this.scope.allGroups.filter((g) => !idGroups.includes(g.id)).map((g) => g.id)
                const removedUsers = this.scope.allUsers
                    .filter((u) => u.groups?.some((g) => removedGroupIds.includes(g.id)))
                    .map((u) => u.id)

                this.scope.users = this.scope.users.filter((u) => !removedUsers.includes(u.userId))

                this._yesAction = NOOP_PROMISE_VOID
            }
        }
    }

    async handleUsersChanged(idUsers: number | null) {
        this.scope.selectedUserId = idUsers
    }

    async handleAddSelectedUser() {
        if (this.scope.selectedUserId) {
            const user = this.scope.allUsers.find((u) => u.id === this.scope.selectedUserId)
            if (user && !this.scope.users?.some((u) => u.userId === user.id)) {
                this.scope.users = [
                    ...(this.scope.users || []),
                    { userId: user.id, userName: user.name, groups: user.groups?.map((g) => g.name).join(', ') }
                ]
            }
            this.scope.selectedUserId = null
        }
    }

    buscarSelecionado = async (notificationId?: number) => {
        if (!notificationId) return

        try {
            this.scope.waiting = new WaitingScope()
            const row = await service.fetchById(notificationId)
            if (row) {
                this.scope.notificationId = row.id
                this.scope.title = row.title
                this.scope.summary = row.summary
                this.scope.content = row.content
                this.scope.style = row.notificationStyle
                this.scope.canMarkAsRead = row.canMarkAsRead
                this.scope.active = row.active
                this.scope.typeId = row.typeId
                this.scope.alert = row.alert
                this.scope.toAllUsers = row.toAllUsers
                this.scope.expiresIn = row.expiresIn
                this.scope.notificationDate = row.notificationDate
                    ? parseLocalDateNoTZ(row.notificationDate.toString())
                    : undefined
                this.scope.groups = row.groups || []
                this.scope.users = row.users || []

                await this.handleTypesChanged()
            }
        } finally {
            this.scope.waiting = undefined
        }
    }
}

export default NotificationTabPresenter
